import axios from 'axios';

// Re-decode Windows-1252/Latin-1 misread UTF-8 bytes back to correct Unicode.
// PostgreSQL returns UTF-8 but browsers sometimes mis-decode as Windows-1252,
// turning e.g. em-dash (U+2014, UTF-8: E2 80 94) into the sequence
// where 0x80 becomes U+20AC (euro sign in Win-1252) instead of staying 0x80.
// Fix: reverse the Win-1252 mapping for the 0x80-0x9F block, then re-decode as UTF-8.
const WIN1252: Record<number, number> = {
  0x20AC:0x80, 0x201A:0x82, 0x0192:0x83, 0x201E:0x84, 0x2026:0x85,
  0x2020:0x86, 0x2021:0x87, 0x02C6:0x88, 0x2030:0x89, 0x0160:0x8A,
  0x2039:0x8B, 0x0152:0x8C, 0x017D:0x8E, 0x2018:0x91, 0x2019:0x92,
  0x201C:0x93, 0x201D:0x94, 0x2022:0x95, 0x2013:0x96, 0x2014:0x97,
  0x02DC:0x98, 0x2122:0x99, 0x0161:0x9A, 0x203A:0x9B, 0x0153:0x9C,
  0x017E:0x9E, 0x0178:0x9F,
};

const fixMojibake = (str: string): string => {
  if (!/[^\x00-\x7F]/.test(str)) return str; // pure ASCII — skip
  try {
    const bytes: number[] = [];
    for (let i = 0; i < str.length; i++) {
      const cp = str.charCodeAt(i);
      if (cp <= 0xFF) {
        bytes.push(cp);
      } else if (WIN1252[cp] !== undefined) {
        bytes.push(WIN1252[cp]); // reverse Win-1252 mapping back to raw byte
      } else {
        return str; // genuine Unicode above Latin-1 — already correct UTF-16
      }
    }
    return new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array(bytes));
  } catch {
    return str; // not valid UTF-8 when re-interpreted — leave unchanged
  }
};

const deepFix = (value: unknown): unknown => {
  if (typeof value === 'string') return fixMojibake(value);
  if (Array.isArray(value)) return value.map(deepFix);
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = deepFix(v);
    }
    return out;
  }
  return value;
};

// Create a custom axios instance with a base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the auth token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common errors and fix encoding
api.interceptors.response.use(
  (response) => {
    if (response.data) {
      response.data = deepFix(response.data);
    }
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors by redirecting to login
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
